/**
 * 通用数据表格组件。
 *
 * 基于 MUI Table 封装，支持列定义、自定义渲染、加载态、空状态与受控分页。
 * 分页为“受控模式”：数据由父组件分页拉取，组件仅负责展示与翻页回调。
 *
 * @module components/common/DataTable
 */
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TablePagination from '@mui/material/TablePagination';
import Paper from '@mui/material/Paper';
import type { ReactNode } from 'react';
import Loading from './Loading';
import EmptyState from './EmptyState';

/** 列定义。 */
export interface Column<T> {
  /** 列唯一键。 */
  key: string;
  /** 列头内容。 */
  header: ReactNode;
  /** 自定义单元格渲染。 */
  render?: (row: T, index: number) => ReactNode;
  /** 对齐方式。 */
  align?: 'left' | 'right' | 'center';
  /** 列宽。 */
  width?: number | string;
}

/** DataTable 组件属性。 */
interface DataTableProps<T> {
  /** 列定义。 */
  columns: Column<T>[];
  /** 当前页数据行。 */
  rows: T[];
  /** 行唯一键取值函数（可选，缺省回退到行索引）。 */
  rowKey?: (row: T) => string | number;
  /** 是否加载中。 */
  loading?: boolean;
  /** 总记录数（用于分页；不传则按 rows 长度）。 */
  total?: number;
  /** 当前页码（0 起始，MUI 语义）。 */
  page?: number;
  /** 每页大小。 */
  pageSize?: number;
  /** 翻页回调（0 起始）。 */
  onPageChange?: (page: number) => void;
  /** 每页大小变更回调。 */
  onPageSizeChange?: (size: number) => void;
  /** 空状态文案。 */
  emptyText?: string;
  /** 紧凑模式。 */
  dense?: boolean;
}

/**
 * 通用数据表格组件。
 *
 * @param props 组件属性。
 * @returns 表格元素。
 */
export default function DataTable<T>({
  columns,
  rows,
  rowKey,
  loading = false,
  total,
  page = 0,
  pageSize = 10,
  onPageChange,
  onPageSizeChange,
  emptyText = '暂无数据',
  dense = false,
}: DataTableProps<T>): JSX.Element {
  const count = total ?? rows.length;
  const colSpan = columns.length;
  const resolveKey = (row: T, index: number): string | number =>
    rowKey ? rowKey(row) : index;

  return (
    <Paper sx={{ overflow: 'hidden' }}>
      <TableContainer>
        <Table size={dense ? 'small' : 'medium'} stickyHeader>
          <TableHead>
            <TableRow>
              {columns.map((col) => (
                <TableCell
                  key={col.key}
                  align={col.align ?? 'left'}
                  style={{ width: col.width }}
                  sx={{ fontWeight: 700, bgcolor: '#fafafa' }}
                >
                  {col.header}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={colSpan} sx={{ border: 0 }}>
                  <Loading />
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={colSpan} sx={{ border: 0 }}>
                  <EmptyState message={emptyText} />
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row, index) => (
                <TableRow key={resolveKey(row, index)} hover>
                  {columns.map((col) => (
                    <TableCell key={col.key} align={col.align ?? 'left'}>
                      {col.render
                        ? col.render(row, index)
                        : (row as Record<string, ReactNode>)[col.key]}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {Boolean(total !== undefined) && (
        <TablePagination
          component="div"
          count={count}
          page={page}
          onPageChange={(_event, newPage) => onPageChange?.(newPage)}
          rowsPerPage={pageSize}
          onRowsPerPageChange={(event) =>
            onPageSizeChange?.(Number(event.target.value))
          }
          rowsPerPageOptions={[5, 10, 20, 50]}
          showFirstButton
          showLastButton
        />
      )}
    </Paper>
  );
}
